import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import {
  getCanceledPlotsThunk,
  getRefundInstallmentsThunk,
  getCancelPlotDetailsThunk,
  selectCanceledPlots,
  selectPagination,
  selectCancelPlotLoading,
  selectCancelPlotError,
  selectGlobalStatistics
} from '../store/slices/plotCancellationSlice'
import { fetchAllUsers } from '../store/slices/authSlice'
import { getAllBookings } from '../store/slices/bookingSlice'
import CancelPlotForm from './CancelPlotForm'
import ProcessRefundModal from './ProcessRefundModal'
import RefundInstallmentsTable from './RefundInstallmentsTable'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  DollarSign,
  Printer,
  MapPin,
  User,
  Plus
} from 'lucide-react'

const CancelPlotDashboard = () => {
  const dispatch = useDispatch()
  const { activeSociety, theme } = useSidebarContext()

  // Redux Selectors
  const canceledPlots = useSelector(selectCanceledPlots)
  const pagination = useSelector(selectPagination)
  const loading = useSelector(selectCancelPlotLoading)
  const error = useSelector(selectCancelPlotError)
  const globalStats = useSelector(selectGlobalStatistics)
  const { users = [] } = useSelector(state => state.auth)
  const { bookings = [] } = useSelector(state => state.booking)
  const selectedPlotDetails = useSelector(state => state.cancelPlot.selectedPlotDetails)

  // Local State
  const [activeTab, setActiveTab] = useState('all') // 'all', 'completed', 'pending'
  const [currentPage, setCurrentPage] = useState(1)
  const [showCancelForm, setShowCancelForm] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [selectedCancelPlotId, setSelectedCancelPlotId] = useState(null)
  const [expandedRowId, setExpandedRowId] = useState(null)

  // Initial Data Fetch
  useEffect(() => {
    dispatch(fetchAllUsers())
  }, [dispatch])

  useEffect(() => {
    if (activeSociety?._id) {
      dispatch(getAllBookings({ limit: 9999, societyId: activeSociety._id }))
    }
  }, [dispatch, activeSociety])

  // Fetch Plots on Filter/Page Change
  useEffect(() => {
    if (activeSociety) {
      const filters = {
        societyId: activeSociety._id,
        page: currentPage,
        limit: 5 // User requested 5 items per page
      }

      if (activeTab === 'completed') filters.completed = 'true'
      if (activeTab === 'pending') filters.completed = 'false'

      dispatch(getCanceledPlotsThunk(filters))
    }
  }, [dispatch, activeSociety, activeTab, currentPage])

  // Fetch Details on Selection
  useEffect(() => {
    if (selectedCancelPlotId) {
      dispatch(getCancelPlotDetailsThunk(selectedCancelPlotId))
      dispatch(getRefundInstallmentsThunk(selectedCancelPlotId))
    }
  }, [dispatch, selectedCancelPlotId])

  // Handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1) // Reset to page 1 on filter change
    setExpandedRowId(null)
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setCurrentPage(newPage)
    }
  }

  const handleRowClick = (id) => {
    if (expandedRowId === id) {
      setExpandedRowId(null)
      setSelectedCancelPlotId(null)
    } else {
      setExpandedRowId(id)
      setSelectedCancelPlotId(id)
    }
  }

  // Format Helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(amount || 0).replace('PKR', 'Rs.')
  }

  return (
    <div className='bg-slate-50 min-h-screen pb-12 transition-colors duration-300 dark:bg-slate-900 font-sans'>

      {/* Header Section */}
      <div className='bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-20 py-4 px-6 shadow-sm'>
        <div className='max-w-7xl mx-auto flex justify-between items-center'>
          <div className='flex items-center space-x-4'>
            <div className='h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-lg shadow-teal-500/30'>
              <FileText size={20} />
            </div>
            <div>
              <h1 className='text-2xl font-bold text-slate-800 dark:text-white tracking-tight'>
                Cancellation Registry
              </h1>
              {activeSociety && (
                <p className='text-sm text-slate-500 dark:text-slate-400 font-medium'>
                  {activeSociety.name}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowCancelForm(true)}
            className='bg-base-content hover:bg-slate-800 text-white px-5 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center font-medium'
          >
            <span className='mr-2 text-lg'>+</span> New Cancellation
          </button>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-6 py-8'>

        {/* Stats Cards Section */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className='bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700'
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='p-2 bg-teal-50 dark:bg-teal-900/30 rounded-lg text-teal-600 dark:text-teal-400'>
                <FileText size={20} />
              </div>
              <span className='text-xs font-medium text-slate-400 uppercase tracking-wider'>Volume</span>
            </div>
            <div className='text-3xl font-bold text-slate-800 dark:text-white mb-1'>
              {globalStats?.count || 0}
            </div>
            <div className='text-sm text-slate-500'>Canceled Plots</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className='bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700'
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='p-2 bg-rose-50 dark:bg-rose-900/30 rounded-lg text-rose-600 dark:text-rose-400'>
                <DollarSign size={20} />
              </div>
              <span className='text-xs font-medium text-slate-400 uppercase tracking-wider'>Outflow</span>
            </div>
            <div className='text-2xl font-bold text-slate-800 dark:text-white mb-1'>
              {formatCurrency(globalStats?.totalRefunded)}
            </div>
            <div className='text-sm text-slate-500'>Total Refunded</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className='bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700'
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400'>
                <Clock size={20} />
              </div>
              <span className='text-xs font-medium text-slate-400 uppercase tracking-wider'>Payable</span>
            </div>
            <div className='text-2xl font-bold text-slate-800 dark:text-white mb-1'>
              {formatCurrency(globalStats?.totalRemaining)}
            </div>
            <div className='text-sm text-slate-500'>Remaining Balance</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className='bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-teal-500/30 dark:border-teal-400/30 bg-gradient-to-br from-white to-teal-50/30 dark:from-slate-800 dark:to-teal-900/10'
          >
            <div className='flex items-center justify-between mb-2'>
              <div className='p-2 bg-teal-500 rounded-lg text-white shadow-lg shadow-teal-500/20'>
                <CheckCircle size={20} />
              </div>
              <span className='text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider'>Revenue</span>
            </div>
            <div className='text-2xl font-bold text-teal-600 dark:text-teal-400 mb-1'>
              {formatCurrency(globalStats?.totalSocietyProfit)}
            </div>
            <div className='text-sm text-slate-600 dark:text-teal-300 font-medium'>Society Profit</div>
          </motion.div>
        </div>

        {/* Filters & Stats Bar */}
        <div className='flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4'>
          {/* Tabs */}
          <div className='bg-white dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700 inline-flex shadow-sm'>
            {['all', 'completed', 'pending'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 capitalize ${activeTab === tab
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
              >
                {tab === 'pending' ? 'In Progress' : tab}
              </button>
            ))}
          </div>

          {/* Quick Stats (Optional, could come from Redux later) */}
          <div className='flex items-center space-x-2 text-sm text-slate-500'>
            <span>Showing {canceledPlots.length} records</span>
          </div>
        </div>

        {/* Main Table Card */}
        <div className='bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden'>
          {/* Table Header */}
          <div className='grid grid-cols-12 gap-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
            <div className='col-span-1'>#</div>
            <div className='col-span-3'>Plot Details</div>
            <div className='col-span-3'>Customer</div>
            <div className='col-span-2 text-right'>Refund Amount</div>
            <div className='col-span-2 text-center'>Status</div>
            <div className='col-span-1 text-center'>Actions</div>
          </div>

          {/* Table Body */}
          <div className='relative min-h-[300px]'>
            {loading ? (
              <div className='absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-10'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500'></div>
              </div>
            ) : null}

            {canceledPlots.length === 0 && !loading ? (
              <div className='flex flex-col items-center justify-center py-20 text-slate-400'>
                <FileText size={48} className='mb-4 opacity-20' />
                <p>No canceled plots found matching your filters.</p>
              </div>
            ) : (
              <AnimatePresence>
                {canceledPlots.map((plot, index) => (
                  <motion.div
                    key={plot._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className='border-b border-slate-100 dark:border-slate-700/50 last:border-0'
                  >
                    {/* Row Content */}
                    <div
                      onClick={() => handleRowClick(plot._id)}
                      className={`grid grid-cols-12 gap-4 px-6 py-4 items-center cursor-pointer transition-colors duration-200 ${expandedRowId === plot._id
                        ? 'bg-teal-50 dark:bg-teal-900/20'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                        }`}
                    >
                      <div className='col-span-1 text-slate-400 font-mono text-xs'>
                        {((currentPage - 1) * 5) + index + 1}
                      </div>
                      <div className='col-span-3'>
                        <div className='font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2 flex-wrap'>
                          <span>Plot {plot.plot_id?.plot_number || 'N/A'}</span>
                          {plot.plot_id?.block && (
                            <span className='px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded text-xs font-medium'>
                              Block {plot.plot_id.block}
                            </span>
                          )}
                        </div>
                        <div className='text-xs text-slate-500 mt-1'>
                          {plot.plot_id?.size && <span>{plot.plot_id.size}</span>}
                          {plot.plot_id?.category && (
                            <span className='ml-2 capitalize'>{plot.plot_id.category}</span>
                          )}
                        </div>
                        <div className='text-xs text-slate-400 mt-0.5'>
                          {new Date(plot.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                      <div className='col-span-3'>
                        <div className='text-sm font-medium text-slate-700 dark:text-slate-300 truncate'>
                          {plot.original_booking?.customer_id?.name || 'Unknown Customer'}
                        </div>
                        <div className='text-xs text-slate-500 truncate mt-0.5'>
                          {plot.original_booking?.customer_id?.contactInfo?.phone || 'No phone'}
                        </div>
                        {plot.original_booking?.customer_id?.contactInfo?.cnic && (
                          <div className='text-xs text-slate-400 truncate mt-0.5'>
                            {plot.original_booking.customer_id.contactInfo.cnic}
                          </div>
                        )}
                      </div>
                      <div className='col-span-2 text-right font-medium text-slate-800 dark:text-slate-200 font-mono'>
                        {formatCurrency((plot.refunded_amount || 0) + (plot.remaining_balance || 0))}
                      </div>
                      <div className='col-span-2 flex justify-center'>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${plot.refund_completed
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                          : 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                          }`}>
                          {plot.refund_completed ? (
                            <> <CheckCircle size={12} /> Completed </>
                          ) : (
                            <> <Clock size={12} /> Pending </>
                          )}
                        </span>
                      </div>
                      <div className='col-span-1 flex justify-center'>
                        <ChevronRight size={16} className={`text-slate-400 transition-transform duration-300 ${expandedRowId === plot._id ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {/* Expanded Details Panel */}
                    <AnimatePresence>
                      {expandedRowId === plot._id && selectedPlotDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className='bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 overflow-hidden'
                        >
                          <div className='p-6 space-y-6'>
                            {/* Header with Actions */}
                            <div className='flex justify-between items-start'>
                              <div>
                                <h3 className='font-semibold text-lg text-slate-800 dark:text-white flex items-center mb-2'>
                                  <AlertCircle size={18} className='mr-2 text-teal-500' />
                                  Complete Refund Details
                                </h3>
                                <p className='text-xs text-slate-500'>Cancelled on {new Date(plot.createdAt).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                              </div>
                              <div className='flex items-center gap-3'>
                                <button
                                  onClick={() => {
                                    const printData = {
                                      plot: plot.plot_id,
                                      customer: plot.original_booking?.customer_id,
                                      booking: plot.original_booking,
                                      cancellation: plot
                                    };
                                    import('./CancelPlotPrint').then(module => {
                                      module.default(printData);
                                    });
                                  }}
                                  className='flex items-center gap-2 px-3 py-2 text-slate-600 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors border border-slate-200 dark:border-slate-700'
                                  title="Print Cancellation Slip"
                                >
                                  <Printer size={16} />
                                  <span className='text-sm'>Print</span>
                                </button>
                                {!plot.refund_completed && (
                                  <button
                                    onClick={() => {
                                      setSelectedCancelPlotId(plot._id);
                                      setShowRefundModal(true);
                                    }}
                                    className='flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors'
                                  >
                                    <DollarSign size={16} />
                                    Process Refund
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Plot & Customer Information Grid */}
                            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                              {/* Plot Details Card */}
                              <div className='bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm'>
                                <h4 className='text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2'>
                                  <MapPin size={16} className='text-teal-500' />
                                  Plot Information
                                </h4>
                                <div className='grid grid-cols-2 gap-4'>
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Plot Number</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.plot_id?.plot_number || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Block</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.plot_id?.block || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Size</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.plot_id?.size || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Category</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white capitalize'>{plot.plot_id?.category || 'N/A'}</p>
                                  </div>
                                  {plot.plot_id?.marla && (
                                    <div>
                                      <p className='text-xs text-slate-500 mb-1'>Marla</p>
                                      <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.plot_id.marla} Marla</p>
                                    </div>
                                  )}
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Plot Type</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.plot_id?.plot_type || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Customer Details Card */}
                              <div className='bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm'>
                                <h4 className='text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2'>
                                  <User size={16} className='text-teal-500' />
                                  Customer Information
                                </h4>
                                <div className='space-y-3'>
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Name</p>
                                    <p className='text-sm font-semibold text-slate-800 dark:text-white'>{plot.original_booking?.customer_id?.name || 'Unknown'}</p>
                                  </div>
                                  <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                      <p className='text-xs text-slate-500 mb-1'>Phone</p>
                                      <p className='text-sm text-slate-700 dark:text-slate-300'>{plot.original_booking?.customer_id?.contactInfo?.phone || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className='text-xs text-slate-500 mb-1'>CNIC</p>
                                      <p className='text-sm text-slate-700 dark:text-slate-300'>{plot.original_booking?.customer_id?.contactInfo?.cnic || 'N/A'}</p>
                                    </div>
                                  </div>
                                  {plot.original_booking?.customer_id?.contactInfo?.email && (
                                    <div>
                                      <p className='text-xs text-slate-500 mb-1'>Email</p>
                                      <p className='text-sm text-slate-700 dark:text-slate-300'>{plot.original_booking.customer_id.contactInfo.email}</p>
                                    </div>
                                  )}
                                  <div>
                                    <p className='text-xs text-slate-500 mb-1'>Booking Date</p>
                                    <p className='text-sm text-slate-700 dark:text-slate-300'>
                                      {plot.original_booking?.booking_date ? new Date(plot.original_booking.booking_date).toLocaleDateString('en-PK') : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Financial Breakdown */}
                            <div className='bg-gradient-to-br from-slate-50 to-teal-50/30 dark:from-slate-800 dark:to-teal-900/10 p-5 rounded-xl border border-teal-200 dark:border-teal-800'>
                              <h4 className='text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2'>
                                <DollarSign size={16} className='text-teal-500' />
                                Financial Breakdown
                              </h4>
                              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                                <div className='bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Total Paid</p>
                                  <p className='text-lg font-bold text-slate-800 dark:text-white'>
                                    {formatCurrency((plot.refunded_amount || 0) + (plot.remaining_balance || 0) + (plot.society_deduction_amount || 0) + (plot.dealer_commission_deducted || 0))}
                                  </p>
                                </div>
                                <div className='bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Society Deduction</p>
                                  <p className='text-lg font-bold text-rose-600 dark:text-rose-400'>
                                    - {formatCurrency(plot.society_deduction_amount || 0)}
                                    {plot.society_deduction_type === 'percentage' && plot.society_deduction_percentage && (
                                      <span className='text-xs font-normal ml-1'>({plot.society_deduction_percentage}%)</span>
                                    )}
                                  </p>
                                </div>
                                <div className='bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Dealer Commission</p>
                                  <p className='text-lg font-bold text-amber-600 dark:text-amber-400'>
                                    - {formatCurrency(plot.dealer_commission_deducted || 0)}
                                  </p>
                                </div>
                                <div className='bg-white dark:bg-slate-800 p-4 rounded-lg border-2 border-teal-500 dark:border-teal-400'>
                                  <p className='text-xs text-teal-600 dark:text-teal-400 font-semibold mb-1'>Net Refund Amount</p>
                                  <p className='text-xl font-bold text-teal-600 dark:text-teal-400'>
                                    {formatCurrency((plot.refunded_amount || 0) + (plot.remaining_balance || 0))}
                                  </p>
                                </div>
                              </div>
                              <div className='mt-4 grid grid-cols-1 md:grid-cols-3 gap-4'>
                                <div className='bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Refunded Amount</p>
                                  <p className='text-base font-semibold text-emerald-600 dark:text-emerald-400'>
                                    {formatCurrency(plot.refunded_amount || 0)}
                                  </p>
                                </div>
                                <div className='bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Remaining Balance</p>
                                  <p className='text-base font-semibold text-amber-600 dark:text-amber-400'>
                                    {formatCurrency(plot.remaining_balance || 0)}
                                  </p>
                                </div>
                                <div className='bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Refund Type</p>
                                  <p className='text-base font-semibold text-slate-800 dark:text-white capitalize'>
                                    {plot.refund_type === 'full' ? 'Full Refund' : 'Installment Refund'}
                                  </p>
                                </div>
                              </div>
                              {plot.cancellation_reason && (
                                <div className='mt-4 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700'>
                                  <p className='text-xs text-slate-500 mb-1'>Cancellation Reason</p>
                                  <p className='text-sm text-slate-700 dark:text-slate-300'>{plot.cancellation_reason}</p>
                                </div>
                              )}
                            </div>

                            {/* Render Installments Table if applicable */}
                            {plot.refund_type !== 'full' && (
                              <div className='mt-4'>
                                <RefundInstallmentsTable
                                  themeColor={theme}
                                  activeSociety={activeSociety}
                                  plotDetails={selectedPlotDetails}
                                />
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>

          {/* Pagination Footer */}
          <div className='border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 px-6 py-4 flex items-center justify-between'>
            <div className='text-sm text-slate-500'>
              Page <span className='font-medium text-slate-800 dark:text-white'>{pagination?.page || 1}</span> of <span className='font-medium text-slate-800 dark:text-white'>{pagination?.totalPages || 1}</span>
            </div>
            <div className='flex gap-2'>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className='p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                <ChevronLeft size={16} className='text-slate-600 dark:text-slate-300' />
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= (pagination?.totalPages || 1)}
                className='p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                <ChevronRight size={16} className='text-slate-600 dark:text-slate-300' />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showRefundModal && (
        <ProcessRefundModal
          cancelPlotId={selectedCancelPlotId}
          onClose={() => setShowRefundModal(false)}
          themeColor={theme}
        />
      )}

      {showCancelForm && (
        <CancelPlotForm
          onClose={() => setShowCancelForm(false)}
          themeColor={theme}
          activeSociety={activeSociety}
          bookings={bookings.filter(b => b.booking_status !== 'Cancelled')}
          users={users}
        />
      )}
    </div>
  )
}

export default CancelPlotDashboard
